import { memo } from 'react';
import { Box, Typography } from '@mui/material';
import LandscapeCell from './LandscapeCell';
import { cellKey, type LandscapeMatrix } from '../hooks/useLandscapeMatrix';

interface LandscapeMatrixProps {
  matrix: LandscapeMatrix;
  onSelectCell: (accountId: string, region: string) => void;
}

/** Row-header column width and per-region min width, in theme spacing units (×8px). */
const ROW_HEADER_UNITS = 25;
const REGION_MIN_UNITS = 15;

const LandscapeMatrixView = memo<LandscapeMatrixProps>(({ matrix, onSelectCell }) => {
  const { accounts, regions, cells } = matrix;

  if (accounts.length === 0 || regions.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No deployments to show for this customer yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ overflow: 'auto', p: 2 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: (theme) =>
            `${theme.spacing(ROW_HEADER_UNITS)} repeat(${regions.length}, minmax(${theme.spacing(REGION_MIN_UNITS)}, 1fr))`,
          gap: 1,
          minWidth: (theme) => theme.spacing(ROW_HEADER_UNITS + regions.length * (REGION_MIN_UNITS + 1)),
        }}
      >
        {/* corner */}
        <Box />
        {/* region headers */}
        {regions.map((r) => (
          <Box key={r} sx={{ px: 1, pb: 0.5, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary' }}>
              {r}
            </Typography>
          </Box>
        ))}

        {/* account rows */}
        {accounts.map((acct) => (
          <Box key={acct.awsAccountId} sx={{ display: 'contents' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', pr: 1, overflow: 'hidden' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>{acct.name}</Typography>
              <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>{acct.awsAccountId}</Typography>
            </Box>
            {regions.map((region) => {
              const key = cellKey(acct.awsAccountId, region);
              return (
                <LandscapeCell
                  key={key}
                  cell={cells.get(key)}
                  onClick={() => onSelectCell(acct.awsAccountId, region)}
                />
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
});

LandscapeMatrixView.displayName = 'LandscapeMatrixView';
export default LandscapeMatrixView;
